import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconButton } from '../button/IconButton';
import { Typography } from '../typography/Typography';
import { Close } from '../private/icons/Close';
import { ErrorFilled } from '../private/icons/ErrorFilled';
import { InfoFilled } from '../private/icons/InfoFilled';
import { ConfirmedFilled } from '../private/icons/ConfirmedFilled';
import { WarningFilled } from '../private/icons/WarningFilled';

const iconMapping = {
  success: <ConfirmedFilled size="small" color="success" />,
  warning: <WarningFilled size="small" color="warning-subtle" />,
  error: <ErrorFilled size="small" color="danger" />,
  info: <InfoFilled size="small" color="info" />,
};

/**
 * An `Alert` informs customers about important changes or persistent conditions.
 * Use this component if you need to communicate important information to a customer
 * or prompt a customer to take an action.
 *
 * Related components: [Typography](#typography), [Link](#link)
 *
 * Usage:
 *
 * ```jsx
 * import { Alert } from '@one-thd/sui-atomic-components';
 * ```
 */
const Alert = React.forwardRef((props, ref) => {

  const {
    action,
    children: childrenProp,
    closeText = 'Close',
    headline,
    onClose,
    size = 'small',
    status,
    ...other
  } = props;

  const isActionable = action || onClose;

  const containerClasses = classNames('sui-mb-4 sui-rounded-base', {
    'sui-bg-danger-subtle': status === 'error',
    'sui-bg-info-subtle': status === 'info',
    'sui-bg-success-subtle': status === 'success',
    'sui-bg-warning-subtle': status === 'warning',
  });

  const barClasses = classNames('sui-w-0 sui-border-solid sui-border-l-4 sui-h-full sui-rounded-base', {
    'sui-border-danger': status === 'error',
    'sui-border-info': status === 'info',
    'sui-border-success': status === 'success',
    'sui-border-warning-subtle': status === 'warning',
  });

  const contentClasses = classNames('sui-flex sui-w-full');

  const iconClasses = classNames('sui-flex sui-items-start', {
    'sui-ml-2 sui-mr-2 sui-pt-2': size === 'small',
    'sui-ml-4 sui-mr-2 sui-pt-6': size === 'large'
  });

  const childrenClasses = classNames('sui-min-w-0 sui-flex sui-overflow-auto sui-leading-[1.286] sui-mr-2', {
    'sui-pt-[6px] sui-pb-2': size === 'small',
    'sui-pt-[22px] sui-pb-6': size === 'large',
    'sui-flex-col': headline,
  });

  const buttonClasses = classNames('sui-flex sui-ml-auto', {
    'sui-mt-4 sui-mr-4': size === 'large'
  });

  const AlertIcon = iconMapping[status];

  let children;
  if (typeof childrenProp === 'string') {
    children = (
      <Typography variant="body-sm" color="primary" component="span">
        {childrenProp}
      </Typography>
    );
  } else {
    children = childrenProp;
  }

  const headlineElement = headline && (
    <Typography variant="body-sm" weight="bold" color="primary" component="span">
      { headline }
    </Typography>
  );

  return (
    <div
      className={containerClasses}
      role="alert"
      ref={ref}
      {...other}
    >
      <div className={contentClasses}>
        {
          size === 'large' && (
            <div className="sui-pl-1 sui-py-1">
              <div className={barClasses} />
            </div>
          )
        }
        {AlertIcon !== null ? (
          <div className={iconClasses}>
            {AlertIcon}
          </div>
        ) : null}
        <div className={childrenClasses}>
          {headlineElement}
          {children}
        </div>
        {isActionable ? (
          <div className={buttonClasses}>
            {action || (
              <IconButton
                icon={Close}
                iconSize="small"
                onClick={onClose}
                aria-label={closeText}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
});

Alert.displayName = 'Alert';

Alert.propTypes = {
  /**
   * The action to display. It renders after the message, at the end of the alert.
   *
   */
  action: PropTypes.node,
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * The label for the close popup icon button
   */
  closeText: PropTypes.string,
  /**
   * The headline to be displayed in the alert.
   */
  headline: PropTypes.string,
  /**
   * Callback fired when the component requests to be closed.
   * Displayed when provided and no `action` prop is set.
   */
  onClose: PropTypes.func,
  /**
   * The status of the alert. This defines the color and icon used.
   */
  status: PropTypes.oneOf(['error', 'info', 'success', 'warning']),
  /**
   * The size of the alert.
   * @default 'small'
   */
  size: PropTypes.oneOf(['small', 'large'])
};

export { Alert };
